import { SITE_URL, TRACKER_OG } from "@/data/site";

export function articleSchema(title: string, description: string, path: string, dateModified: string, image = TRACKER_OG) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    datePublished: dateModified,
    dateModified,
    image,
    author: { "@type": "Organization", name: "PeptideX", url: SITE_URL },
    publisher: { "@type": "Organization", name: "PeptideX", url: SITE_URL },
    mainEntityOfPage: `${SITE_URL}${path}`,
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function faqSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#org`,
  name: "PeptideX",
  legalName: "VitalBridgeX Holdings LLC",
  url: SITE_URL,
  description: "PeptideX is a brand of VitalBridgeX Holdings LLC, a US company.",
  sameAs: ["https://x.com/peptidexai"],
};

type DrugInfo = {
  name: string;
  nonProprietaryName?: string;
  description?: string;
  drugClass?: string;
  manufacturer?: string;
  approved: boolean;
  approvalNote?: string;
};

/** MedicalWebPage about a Drug. Unapproved compounds get no legal/prescription status, only "investigational" wording. */
export function medicalPageSchema(opts: { name: string; description: string; path: string; lastReviewed: string; drug: DrugInfo; citations?: string[] }) {
  const { drug } = opts;
  const drugNode: Record<string, unknown> = {
    "@type": "Drug",
    name: drug.name,
    ...(drug.nonProprietaryName ? { nonProprietaryName: drug.nonProprietaryName } : {}),
    ...(drug.drugClass ? { drugClass: { "@type": "DrugClass", name: drug.drugClass } } : {}),
    ...(drug.manufacturer ? { manufacturer: { "@type": "Organization", name: drug.manufacturer } } : {}),
  };
  if (drug.approved) {
    drugNode.isProprietary = true;
    drugNode.prescriptionStatus = "https://schema.org/PrescriptionOnly";
    drugNode.legalStatus = { "@type": "DrugLegalStatus", name: drug.approvalNote ?? "FDA-approved", applicableLocation: { "@type": "Country", name: "US" } };
    if (drug.description) drugNode.description = drug.description;
  } else {
    drugNode.description = `Investigational or not FDA-approved. ${drug.description ?? ""}`.trim();
  }
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: opts.name,
    description: opts.description,
    url: `${SITE_URL}${opts.path}`,
    lastReviewed: opts.lastReviewed,
    dateModified: opts.lastReviewed,
    reviewedBy: { "@type": "Organization", name: "The PeptideX team" },
    audience: { "@type": "PeopleAudience", audienceType: "Patient" },
    medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
    about: drugNode,
    publisher: { "@id": `${SITE_URL}/#org`, "@type": "Organization", name: "PeptideX", url: SITE_URL },
    ...(opts.citations?.length ? { citation: opts.citations } : {}),
  };
}
